import { Injectable } from '@angular/core';
import { customProductList, defaultProductList } from '@catalog/const';
import { CityActions } from '@city/data-access';
import { CustomRouterStateSnapshot } from '@core/const';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED, ROUTER_REQUEST, RouterNavigationAction, RouterRequestAction } from '@ngrx/router-store';
import { OccUnitPricesResponse } from '@product/const';
import { CustomProductActions, OccProductService } from '@product/data-access';
import { mapBonuses, mapUnitPrices } from '@product/utils';
import { Observable, combineLatest, from } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap } from 'rxjs/operators';

@Injectable()
export class PricesEffects {
  load$: Observable<
    | CustomProductActions.LoadPricesSuccess
    | CustomProductActions.LoadBonusesSuccess
    | CustomProductActions.LoadPricesFail
    | CustomProductActions.LoadBonusesFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomProductActions.LOAD_PRICES),
      mergeMap((action: CustomProductActions.LoadPrices) => {
        const { productCode } = action;

        return this.occProductService.loadUnitPrices(productCode).pipe(
          map(({ unitPrices }: OccUnitPricesResponse) => unitPrices),
          map((unitPrices) => ({
            prices: mapUnitPrices(unitPrices),
            bonuses: mapBonuses(unitPrices),
          })),
          switchMap((data) => {
            return from([
              new CustomProductActions.LoadPricesSuccess(productCode, data.prices),
              new CustomProductActions.LoadBonusesSuccess(productCode, data.bonuses),
            ]);
          }),
          catchError((e: unknown) =>
            from([
              new CustomProductActions.LoadPricesFail(productCode, e),
              new CustomProductActions.LoadBonusesFail(productCode, e),
            ]),
          ),
        );
      }),
    ),
  );

  /**
   * Очистка единиц измерения, только при осуществлении перехода из листинга на другие страницы
   */
  cleanupPrices$: Observable<CustomProductActions.ResetAllPrices | CustomProductActions.ResetALLBonuses> = createEffect(
    () =>
      combineLatest([this.actions$.pipe(ofType(ROUTER_REQUEST)), this.actions$.pipe(ofType(ROUTER_NAVIGATED))]).pipe(
        filter(
          ([request, navigation]: [
            RouterRequestAction<CustomRouterStateSnapshot>,
            RouterNavigationAction<CustomRouterStateSnapshot>,
          ]) => {
            const catalogRoutes: string[] = [...customProductList, ...defaultProductList];

            return (
              catalogRoutes.includes(request.payload.routerState.semanticRoute) &&
              !catalogRoutes.includes(navigation.payload.routerState.semanticRoute)
            );
          },
        ),
        switchMap(() => {
          return from([new CustomProductActions.ResetAllPrices(), new CustomProductActions.ResetALLBonuses()]);
        }),
      ),
  );

  /**
   * Очистка единиц измерения, при переключении города
   */
  cleanupPricesInNewCity$: Observable<CustomProductActions.ResetAllPrices | CustomProductActions.ResetALLBonuses> =
    createEffect(() =>
      this.actions$.pipe(ofType(CityActions.SET_ACTIVE_BASE_STORE)).pipe(
        switchMap(() => {
          return from([new CustomProductActions.ResetAllPrices(), new CustomProductActions.ResetALLBonuses()]);
        }),
      ),
    );

  constructor(
    private actions$: Actions,
    private occProductService: OccProductService,
  ) {}
}
